/**
 * Vitrine de centros de treinamento.
 *
 * Só aparecem centros que ligaram a visibilidade no painel deles — ver
 * `Docs/internal/business/training-centers/public-profile/`.
 */

/**
 * Fallback de exibição para ids crus nos cards/detalhe.
 *
 * O catálogo de verdade (chips de filtro) vem do backend via
 * `modalitiesManager`; este mapa só traduz ids conhecidos quando a tela não
 * tem o manager à mão, e devolve o id cru para modalidades novas.
 */
const MODALITY_LABELS: Record<string, string> = {
  soccer: 'Futebol',
  tennis: 'Tênis',
  volleyball: 'Vôlei',
  footvolley: 'Futevôlei',
  beach_tennis: 'Beach Tênis'
}

export function getModalityLabel(modality: string): string {
  return MODALITY_LABELS[modality] ?? modality
}

const LEVEL_LABELS: Record<string, string> = {
  beginner: 'Iniciante',
  'lower-intermediate': 'Intermediário baixo',
  intermediate: 'Intermediário',
  'upper-intermediate': 'Intermediário avançado',
  advanced: 'Avançado'
}

export function getLevelLabel(level: string): string {
  return LEVEL_LABELS[level] ?? level
}

/**
 * Cores por nível na convenção de dificuldade (pista de esqui): começa no
 * verde suave e esquenta até o vermelho. A escala anterior terminava o
 * avançado em verde — a cor que o resto do app usa para "positivo/fácil".
 */
const LEVEL_TAG_CLASSES: Record<string, string> = {
  beginner: 'bg-emerald-50 text-emerald-700',
  'lower-intermediate': 'bg-teal-50 text-teal-700',
  intermediate: 'bg-amber-50 text-amber-700',
  'upper-intermediate': 'bg-orange-100 text-orange-700',
  advanced: 'bg-red-100 text-red-700'
}

export function getLevelTagClass(level: string): string {
  return LEVEL_TAG_CLASSES[level] ?? 'bg-slate-100 text-slate-700'
}

const WEEKDAY_LABELS: Record<number, string> = {
  1: 'Domingo',
  2: 'Segunda',
  3: 'Terça',
  4: 'Quarta',
  5: 'Quinta',
  6: 'Sexta',
  7: 'Sábado'
}

export function getWeekdayLabel(weekday: number): string {
  return WEEKDAY_LABELS[weekday] ?? ''
}

const WEEKDAY_SHORT_LABELS: Record<number, string> = {
  1: 'Dom',
  2: 'Seg',
  3: 'Ter',
  4: 'Qua',
  5: 'Qui',
  6: 'Sex',
  7: 'Sáb'
}

/** Rótulo curto para o cabeçalho de coluna da agenda semanal. */
export function getWeekdayShortLabel(weekday: number): string {
  return WEEKDAY_SHORT_LABELS[weekday] ?? ''
}

/** Minutos desde a meia-noite → `HH:MM`. Horário local do centro, sem conversão. */
export function formatMinutes(minutes: number): string {
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return `${String(hours).padStart(2, '0')}:${String(rest).padStart(2, '0')}`
}

export interface TrainingCenterListItem {
  id: string
  name: string
  logoURL?: string | null
  coverImageURL?: string | null
  modalities: string[]
  locationName?: string | null
  /** Quantos locais de treino o centro tem — o card não mostra endereço porque
   *  um centro pode ter vários, e nomear só um engana. */
  locationsCount: number
  distanceMeters?: number | null
  offersRemoteConsulting: boolean
}

export interface TrainingCenterLocation {
  id: string
  name: string
  address: string
  latitude: number
  longitude: number
}

export interface TrainingCenterScheduleClass {
  id: string
  weekday: number
  startTimeMin: number
  endTimeMin: number
  modality?: string | null
  level?: string | null
  description?: string | null
  locationId?: string | null
  /** `null` = turma sem limite de alunos. */
  maxParticipants?: number | null
  /** `null` = sem limite. Zero significa turma cheia. */
  availableSpots?: number | null
}

export interface TrainingCenterDetail {
  id: string
  name: string
  publicDescription?: string | null
  logoURL?: string | null
  coverImageURL?: string | null
  modalities: string[]
  whatsappNumber?: string | null
  instagramHandle?: string | null
  offersRemoteConsulting: boolean
  locations: TrainingCenterLocation[]
  weeklySchedule: TrainingCenterScheduleClass[]
  levels: string[]  /**
   * Usuário logado é cliente do centro; ausente sem auth. Gate do CTA
   * "Marcar aula" — quem não é cliente vê o contato.
   */
  isCustomer?: boolean | null
}

export interface ListTrainingCentersFilters {
  q?: string
  lat?: number
  lng?: number
  radius?: number
  modalities?: string[]
  remote?: boolean
}
